/** CrmRecordPanelContainer (Item D / D2b) — the data + mutation owner for the record panel.
 *
 * Hydrates the authoritative `record_field_states.v1` payload (`useCrmRecord`) and only then passes
 * `fieldStates` down to the presentational CrmRecordPanel, which renders actionable controls (§6.5
 * hydrate-before-actionable). While the endpoint is in flight the panel paints read-only from the
 * grid-row snapshot (R3). Edits / accept / dismiss route through the shared mutation hooks
 * (`useSetFieldValue`, `useReviewAcceptProposal`, `useReviewRejectProposal`) — which invalidate
 * `['crm','grid',listId]`, `['crm','record',listId]` AND `['crm','record-v2',listId]`, so the grid
 * behind and the panel (which renders from v2 when enabled) stay in lockstep after any mutation.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import { isAdmin } from '../../auth/admins';
import { useAuthUser } from '../../auth/useAuthUser';
import {
  useAcceptRecord, useCrmListOptions, useCrmRecord, useCrmRecordLayout, useCrmRecordV2,
  useDismissRecord, useDraftEditField, usePassRecord, useRenameDraftRecord,
  useReviewAcceptAll,
  useReviewDismissAllAi, useAddProjectedFact,
  useReviewAcceptProposal, useReviewEditAcceptProposal, useReviewRejectProposal,
  useInterpretFact, useProjectedFactAction, useRetypeProjectedFact,
  useReviewRestoreProposal, useSetFieldValue,
  useSetRecordLayout,
} from '../../hooks/useCrm';
import { useViewParam } from '../../hooks/navigation/useUrlState';
import type {
  CrmFactInterpretResponse, CrmGridColumn, CrmProjectedFact, CrmRecordFieldState,
  CrmRecordLayoutSection, RecordReviewV2Field,
} from '../../types';
import { DismissReasonModal } from '../pages/DismissReasonModal';
import { LogPassModal } from '../pages/DealsPage.modals';
import { CrmDealMergeModal } from './CrmDealMergeModal';
import { cellText } from './crmCells';
import { useMyTabs } from '../../hooks/useAdmin';
import { adaptRecordReviewV2, isResolvedCommittedMember } from './recordReviewV2';
import { CrmEvidenceTimeline } from './CrmEvidenceTimeline';
import { CrmNotesSection } from './CrmNotesSection';
import { CrmPortcoLink } from './CrmPortcoLink';
import { CrmRecentUpdates } from './CrmRecentUpdates';
import { CrmRecordDealUpdates } from './CrmRecordDealUpdates';
import { CrmRecordLayoutEditor } from './CrmRecordLayoutEditor';
import { CrmRecordPanel } from './CrmRecordPanel';
import { CrmPartyFilterProvider } from './crmPartyFilter';
import { CrmRecordTimeline } from './CrmRecordTimeline';
import { CrmTimeline } from './CrmTimeline';
import { refLabel, refLocalId, visibleRefs } from './EntityRefs';
import type { MentionContextTarget } from './crmNoteMention';
import styles from './crm.module.css';

/** Related people/organizations already disclosed in this governed record payload. These are only
 * ranking hints for mention search; the server re-runs every target through its read gate. */
function noteMentionContext(record: Record<string, unknown> | undefined,
                            columns: CrmGridColumn[]): MentionContextTarget[] {
  if (!record) return [];
  const out: MentionContextTarget[] = [];
  const seen = new Set<string>();
  for (const col of columns) {
    const kind = col.field_type.startsWith('person') ? 'person'
      : col.field_type.startsWith('organization') ? 'organization' : null;
    if (!kind) continue;
    for (const ref of visibleRefs(record[col.field_key])) {
      const id = refLocalId(ref, kind);
      const name = refLabel(ref);
      const key = `${kind}:${id}`;
      if (id == null || name == null || seen.has(key)) continue;
      seen.add(key);
      out.push({ kind, id, name });
      if (out.length >= 100) return out;
    }
  }
  return out;
}

export interface RecordPanelContainerProps {
  listId: number;
  entryId: number;
  /** Fast grid-row snapshot for the instant read-only paint (R3); undefined for a not-in-loaded-set
   * deep link (the panel then shows a loading state until the endpoint hydrates). */
  record: Record<string, unknown> | undefined;
  columns: CrmGridColumn[];
  position: { index: number; total: number } | null;
  staleInView?: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onOpenProfile?: (kind: 'person' | 'organization', id: number) => void;
  /** Open an email detail from the panel's Timeline sub-view (D6) — threaded from CrmPage's openMsg
   * so a timeline email row navigates to the msg view (sel swap → drops rec/rtab per §7). */
  onOpenMsg?: (emailId: number) => void;
}

export function CrmRecordPanelContainer({
  listId, entryId, record: snapshot, columns: snapshotCols, onOpenMsg, ...nav
}: RecordPanelContainerProps) {
  const rec = useCrmRecord(listId, entryId);
  // R4-1: hydrate the governed record_review.v2 field-review for ANY row — PROPOSED *or* COMMITTED —
  // when the flag is on. Gated on the v1 `record_review_v2` capability so the panel never fires a v2
  // fetch that 404s while the flag is dark (R1d note): v1 loads first (fast/cached) → capability → v2
  // fetch. DARK (flag off): capability false → v2 never fetched → the v1 path renders (byte-identical
  // to today). recV2's error state is IGNORED for notFound (that binds to the v1 record only).
  const v2Enabled = rec.data?.capabilities?.record_review_v2 === true;
  const recV2 = useCrmRecordV2(listId, entryId, v2Enabled);
  const listOptions = useCrmListOptions(listId);
  const setFieldValue = useSetFieldValue(listId);
  // R5/R4-1 per-field review lifecycle routes: draft-override edit (proposed) + edit-and-accept
  // (committed) + reject + restore. The edit handler routes to the right one by record lifecycle.
  const draftEdit = useDraftEditField(listId);
  const editAcceptProposal = useReviewEditAcceptProposal(listId);
  const restoreProposal = useReviewRestoreProposal(listId);
  const acceptAll = useReviewAcceptAll(listId);  // S3/S4: bulk-accept a committed record's pending updates
  const dismissAllAi = useReviewDismissAllAi(listId);  // P1: and its disposal twin
  // P1: "Dismiss AI" throws work away, so it is never one click. Two-step on the button itself rather
  // than a fifth modal — there is no input to collect, only an intent to confirm.
  const [dismissAiArmed, setDismissAiArmed] = useState(false);
  // notes-provenance: the record panel's in-cell Accept/Dismiss route through the NEW lifecycle
  // resolver (settled value layer; CRM-note materialization for blocks) — NOT the legacy overlay +
  // Affinity push. The CRM surface is duplicative + never publishes to Affinity (spec §0).
  const acceptProposal = useReviewAcceptProposal(listId);
  const dismissProposal = useReviewRejectProposal(listId);
  const setRecordLayout = useSetRecordLayout(listId);
  // List-level record layout on its own query (warmed at grid-load) so the panel paints fields in the
  // FINAL custom order from frame one — the record's bundled layout only arrives on hydration, which
  // is what caused the open-time field reorder "jitter". Falls back to the bundled layout.
  const recordLayout = useCrmRecordLayout(listId);
  // Item 3b: whole-record actions on a PROPOSED row (Accept = promote, Pass = promote-to-Passed,
  // Dismiss = suppress, Merge = fold into an existing deal).
  const acceptRecord = useAcceptRecord(listId);
  const dismissRecord = useDismissRecord(listId);
  const renameDraft = useRenameDraftRecord(listId);
  const passRecord = usePassRecord(listId);
  // ⚠ Each of these holds the ENTRY ID it was opened for, not a boolean, and every render gate
  // below compares it to the CURRENT `entryId`. Adversarial review (F.8) found the wrong-record
  // bug this closes: the panel instance survives a ‹ › repoint by design (it must not steal
  // focus mid-browse), so a plain `open` boolean left a confirm dialog stranded over a record the
  // operator never chose — and every mutate() below reads the CURRENT entryId. Confirm then wrote
  // against the new record: a terminal Log Pass on the wrong deal, in the worst case. Keying the
  // modal to its record makes that unreachable rather than merely unlikely, which is why it is
  // done here as well as in the key guard — the ‹ › BUTTONS are another route to the same state.
  const [dismissFor, setDismissFor] = useState<number | null>(null);
  const [passFor, setPassFor] = useState<number | null>(null);
  const [mergeFor, setMergeFor] = useState<number | null>(null);
  // R5: the per-field reject-reason modal target (proposed-row field). Transient (useState, not URL).
  const [fieldRejectTarget, setFieldRejectTarget] = useState<
    { entryId: number; fieldKey: string; proposalId: number | null; label: string } | null>(null);

  // Admin gate for the layout editor. useAuthUser returns the REAL authenticated user (survives
  // impersonation) — matching the backend require_admin gate (actual_email); the server is the
  // authoritative check. The editor itself is a transient modal (useState, not URL state).
  const canEditLayout = isAdmin(useAuthUser().email);
  const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
  const [layoutError, setLayoutError] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyField, setBusyField] = useState<string | null>(null);

  // Synchronous double-submit guard. The action buttons are aria-disabled (NOT `disabled`) so they
  // keep focus while a write is in flight (a11y — see CrmRecordPanel), which means their onClick still
  // fires; this ref makes a second activation before the first settles a reliable no-op (React state
  // wouldn't have flushed yet within the same tick, so busyField alone can't gate it).
  const inFlightRef = useRef(false);
  const begin = useCallback((fieldKey: string) => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setActionError(null);
    setBusyField(fieldKey);
    return true;
  }, []);
  const end = useCallback(() => { inFlightRef.current = false; setBusyField(null); }, []);

  const data = rec.data;
  const record = data?.record ?? snapshot;
  const columns = data?.columns ?? snapshotCols;
  const editMode = data?.list.edit_mode;
  const options = listOptions.data?.options;
  // PR3 item 7: the roster picklists for `ref_options_source:'roster'` ref columns, from the same
  // options query as the enum picklists.
  const refOptions = listOptions.data?.ref_options;
  // plan 1b: the Stage ladder membership, from the SAME options query as the picklists above.
  const stageLadders = listOptions.data?.stage_ladders ?? null;
  const notFound = rec.isError && rec.error instanceof ApiError && rec.error.status === 404;

  const fieldStates = useMemo(() => {
    if (!data) return undefined;
    const m: Record<string, CrmRecordFieldState> = {};
    for (const s of data.field_states) m[s.field_name] = s;
    return m;
  }, [data]);

  // R4-1: adapt the governed v2 payload (present for a proposed OR committed row with the flag on) →
  // the panel's per-field review map (by field_name) + the R2b record-level CAS token. Undefined while
  // dark → the panel renders the v1 path. ReviewFieldRow now handles BOTH lifecycles (committed fields
  // get the field-level Accept via allowed_actions; proposed fields don't), so the earlier proposed-only
  // gate (a stopgap when ReviewFieldRow lacked Accept) is removed — a committed payload renders through
  // ReviewFieldRow correctly, and the proposed-committed-race half-state no longer applies.
  const v2Adapted = useMemo(() => (recV2.data ? adaptRecordReviewV2(recV2.data) : undefined), [recV2.data]);
  const isProposedPayload = v2Adapted?.recordLifecycle === 'proposed';
  const reviewFields = useMemo(() => {
    if (!v2Adapted) return undefined;
    const m: Record<string, RecordReviewV2Field> = {};
    for (const f of v2Adapted.fields) {
      // PROPOSED rows: route ALL fields (projection + draft-override; CRM-local, nothing pushes).
      // COMMITTED rows: route every MEMBER field — a live PENDING proposal (reject-with-reason +
      // edit-and-accept, unchanged) and, since D4/U2 (review 2026-08-21), a RESOLVED one too, so the
      // panel offers the plain manual edit the server now sanctions (`allowed_actions: ['edit']` on
      // accepted / superseded / edited). The old pending-only gate is what left a resolved field to the
      // v1 fallback, where a client-side type check — not `allowed_actions` — decided editability and
      // the server's action model was never read (finding U3).
      //   EXCEPTION, deliberately still on v1: a field whose external sync FAILED. ReviewFieldRow has
      // no push_error / Retry affordance, and the v1 row is the only place that error is reachable, so
      // a failed push keeps its field on v1 exactly as today. PENDING is exempt from the exception so
      // its routing stays byte-identical to before this change.
      //   `isResolvedCommittedMember` — NOT `disposition !== 'pending'`: an OVERLAID pending proposal
      // (edit-and-accept in flight) also reports `edited`, and it must keep its v1 row, where the blue
      // dot and the Retry live and where its pencil already meant a plain edit. Routing it to v2 would
      // have made it READ-ONLY (v2 offers it no 'edit'), which is the exact R4-1 regression the old
      // pending-only gate existed to avoid.
      if (isProposedPayload || f.disposition === 'pending'
          || (isResolvedCommittedMember(f, isProposedPayload)
              && f.external_sync?.status !== 'failed')) {
        m[f.field_name] = f;
      }
    }
    return m;
  }, [v2Adapted, isProposedPayload]);
  const reviewCas = v2Adapted?.reviewCas;

  // A stale write (a newer proposal landed since the card rendered) → 409: refetch the authoritative
  // record(s) so the panel shows the latest, and tell the operator to re-apply (same UX as the Deals
  // tab). Refetches BOTH v1 and (when active) the v2 review read so the field grid re-resolves.
  const applyError = useCallback((e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.status === 409) {
      rec.refetch();
      recV2.refetch();
      // Not every 409 is the CAS. `dismiss/pass/rename/merge-record` raise a 409 whose detail is a
      // STRING that already names the situation and its recovery ("record is already committed —
      // reload the list"); the generic message used to swallow all of them and tell the operator to
      // "re-apply your change", which for an already-committed record is advice to redo a write that
      // has no reason to succeed. Prefer the server's own sentence; the generic line is for the
      // STRUCTURED CAS payload (`{stale, scope, …}`), which has no operator-legible text of its own.
      const casDetail = (e as { detail?: { detail?: unknown } }).detail?.detail;
      setActionError(typeof casDetail === 'string' && casDetail
        ? casDetail
        : 'This record changed since you opened it — showing the latest. Re-apply your change if needed.');
      return;
    }
    const detail = (e as { detail?: { detail?: string | { message?: string } } } | null)?.detail?.detail;
    if (typeof detail === 'string') { setActionError(detail); return; }
    // ⚠ The fact routes raise `HTTPException(400, detail={"error": …, "message": …})`, so `detail.detail`
    // is an OBJECT and the string check above discards it — the operator got a generic fallback while
    // the ledger's actual reason ("that value was previously rejected on this deal…", which NAMES the
    // recovery) was thrown away. That message is the whole point of the server turning a silent
    // no-write into an explicit refusal.
    const message = (detail as { message?: unknown } | undefined)?.message;
    setActionError(typeof message === 'string' && message ? message : fallback);
  }, [rec, recV2]);

  const onEditField = useCallback((fieldKey: string, value: unknown) => {
    if (!begin(fieldKey)) return;
    setFieldValue.mutate({ entryId, field_key: fieldKey, value }, {
      onError: (err) => applyError(err, 'Edit failed — the value was not saved.'),
      onSettled: end,
    });
  }, [entryId, setFieldValue, applyError, begin, end]);

  // Draft rename (owner request 2026-08-18): the title pencil on a PROPOSED row. Candidate data,
  // not a cell — the D8 rule refuses set_field_value on drafts — so it takes its own route. Reuses
  // the `name` busy slot so a double-submit is blocked the same way a cell edit's is.
  const onRenameDraft = useCallback((name: string) => {
    if (!begin('name')) return;
    renameDraft.mutate({ entryId, name }, {
      onError: (err) => applyError(err, 'Rename failed — the name was not saved.'),
      onSettled: end,
    });
  }, [entryId, renameDraft, applyError, begin, end]);

  const onAcceptProposal = useCallback((fieldKey: string, proposalId: number | null) => {
    if (proposalId == null || !begin(fieldKey)) return;
    // reviewCas is set only when the v2 review is active (committed row, flag on) → engages the R2b
    // record-level CAS; undefined on the v1 path → per-proposal CAS only (unchanged legacy request).
    acceptProposal.mutate({ entryId, proposalId, cas: reviewCas }, {
      onError: (err) => applyError(err, 'Could not accept the proposal.'),
      onSettled: end,
    });
  }, [entryId, acceptProposal, reviewCas, applyError, begin, end]);

  const onDismissProposal = useCallback((fieldKey: string, proposalId: number | null) => {
    if (proposalId == null || !begin(fieldKey)) return;
    dismissProposal.mutate({ entryId, proposalId }, {
      onError: (err) => applyError(err, 'Could not dismiss the proposal.'),
      onSettled: end,
    });
  }, [entryId, dismissProposal, applyError, begin, end]);

  // `onRetryPush` lived here — it fired `useSyncList` to re-drain the list's pending overlay behind
  // the per-field "Retry" button. Deleted with the drain by the Pillar-D write funeral
  // (crm-of-record-plan §5, 2026-08-27). Nothing sets `push_error` any more, so the banner that
  // carried the button is itself unreachable; the v1-routing exception for failed-sync fields (see
  // above) is left in place deliberately — it is behaviour-neutral now and rewriting live routing
  // rules is not this change's business.

  // ── R5/R4-1 per-field review handlers (edit / reject / restore) ────────────────────────────────
  // Each echoes the R2b CAS (reviewCas) so a newer proposal-set since render → 409 → applyError
  // refetches + prompts re-apply. Reject opens the reason modal (fires on confirm); edit/restore fire
  // immediately behind the in-flight guard.
  //
  // Edit routes by record lifecycle × whether the field has a proposal (R4-1):
  //   • PROPOSED row                      → non-publishing DRAFT override (draft-edit, by field_key).
  //   • COMMITTED row, PENDING proposal   → EDIT-AND-ACCEPT (publishes the operator's value, by
  //                                         proposal_id). Unchanged — decision D3 is open.
  //   • COMMITTED row, RESOLVED proposal  → plain field edit (D4). ⛔ NOT edit-and-accept: the
  //     per-proposal CAS in proposal_lifecycle requires `resolution IS NULL`, so an accepted /
  //     superseded / edited / rejected proposal 409s. The field is a plain committed field again.
  //   • COMMITTED row, no prop            → plain field edit (overlay+push) via onEditField.
  const onReviewEditField = useCallback((fieldKey: string, value: unknown) => {
    const rf = reviewFields?.[fieldKey];
    const proposalId = rf?.proposal_id ?? null;
    // The route is chosen by the SAME predicate that chose the row + rendered the pencil (D4): a
    // resolved member takes the plain PATCH, everything else with a proposal_id is a live proposal and
    // keeps edit-and-accept. Deriving it any other way is how the panel starts rendering one story and
    // writing another.
    const isLiveProposal = rf ? !isResolvedCommittedMember(rf, isProposedPayload) : false;
    if (isProposedPayload) {
      if (!begin(fieldKey)) return;
      draftEdit.mutate({ entryId, fieldKey, value, cas: reviewCas }, {
        onError: (err) => applyError(err, 'Draft edit failed — the value was not saved.'),
        onSettled: end,
      });
    } else if (proposalId != null && isLiveProposal) {
      if (!begin(fieldKey)) return;
      editAcceptProposal.mutate({ entryId, proposalId, value, cas: reviewCas }, {
        onError: (err) => applyError(err, 'Edit failed — the value was not saved.'),
        onSettled: end,
      });
    } else {
      // Committed field with no proposal, or one whose proposal is already RESOLVED (D4) → plain edit
      // (owns its begin/end).
      onEditField(fieldKey, value);
    }
  }, [entryId, reviewFields, isProposedPayload, draftEdit, editAcceptProposal, reviewCas,
      applyError, begin, end, onEditField]);

  const onRestoreProposal = useCallback((fieldKey: string, proposalId: number | null) => {
    if (proposalId == null || !begin(fieldKey)) return;
    restoreProposal.mutate({ entryId, proposalId, cas: reviewCas }, {
      onError: (err) => applyError(err, 'Could not restore the proposal.'),
      onSettled: end,
    });
  }, [entryId, restoreProposal, reviewCas, applyError, begin, end]);

  // ── E2e-3 §4.3.6: the tri-action on a PROJECTED column, straight onto the ledger commands. ──
  // `expected_revision` comes from the fact the panel RENDERED, not from a re-read: the whole point
  // of the CAS is that it describes what the operator was looking at. A 409 routes through
  // `applyError`, which refetches and tells them to re-apply — the same stale-card contract the
  // proposal actions use, and the reason a stale Reject cannot retract a company nobody saw.
  const factAction = useProjectedFactAction(listId);
  // ⚠ ONE fact, not the chip's whole list — and the change from `chip.facts` to `[fact]` is the point
  // of §1.2 rather than a refactor. Sending every member was right for the inline control, which could
  // only mean "this value is wrong"; it is wrong for everything the modal does, because a company that
  // is both the target and its own adviser needs each of those corrected on its own. The `facts` array
  // stays an array because the endpoint's contract is a list — it now has one element.
  const runFactAction = useCallback((fieldKey: string, fact: CrmProjectedFact,
                                     action: 'reject' | 'accept' | 'edit', localId?: number,
                                     qualifiers?: Record<string, string>, reason?: string) => {
    if (!begin(fieldKey)) return;
    const facts = [{ assertion_id: fact.assertion_id, revision: fact.revision }];
    factAction.mutate({ entryId, action, facts, localId, qualifiers, reason }, {
      onError: (err) => applyError(err, `Could not ${action} that value.`),
      onSuccess: (d) => {
        // The recompute is best-effort server-side, so say so rather than leaving the operator with a
        // ledger that changed and a grid cell that did not.
        if (d?.reprojection_incomplete) {
          setActionError('Saved, but the list cell could not be refreshed — reload to see it.');
        }
      },
      onSettled: end,
    });
  }, [entryId, factAction, applyError, begin, end]);
  // ⚠ The remove-reason MODAL is gone from here, and its obligation is not.
  //
  // It existed because the chip row had nowhere to ask "why are you removing this" — §0's third cause.
  // §1.2 gives the question a home inside the fact editor, which reveals the reason inline and refuses
  // to enable Remove without one. Keeping a second confirm on top would mean two dialogs for one
  // removal. The requirement is unchanged and still enforced twice over: the command refuses a blank
  // reason, and so does the modal's button.
  const onRemoveFact = useCallback(
    (fk: string, fact: CrmProjectedFact, reason: string) =>
      runFactAction(fk, fact, 'reject', undefined, undefined, reason),
    [runFactAction]);
  const onConfirmFact = useCallback(
    (fk: string, fact: CrmProjectedFact) => runFactAction(fk, fact, 'accept'),
    [runFactAction]);
  const onRepointFact = useCallback(
    (fk: string, fact: CrmProjectedFact, localId: number) =>
      runFactAction(fk, fact, 'edit', localId),
    [runFactAction]);
  // A role correction: same entity, new qualifier. ONE ledger command — never remove-then-add, which
  // would break the evidence chain between the two halves.
  const onSaveFactRole = useCallback(
    (fk: string, fact: CrmProjectedFact, qualifiers: Record<string, string>) =>
      runFactAction(fk, fact, 'edit', undefined, qualifiers),
    [runFactAction]);
  // §1.3 — the describe-it box. READ-ONLY: it returns a draft that fills the modal's controls, and the
  // write still goes through `onAddFact` (the same `facts/add` the controls use), so every gate on that
  // path applies to an accepted parse exactly as it applies to a hand-filled one.
  const interpretFact = useInterpretFact(listId);
  const [interpretError, setInterpretError] = useState<string | null>(null);
  // §7 Q1's switch is a SERVER fact, and the record payload has no capability for it (that dict is
  // assembled in `lists.py`, which this worker does not touch). So the box asks once and, if the
  // server says it is off, stops offering itself — better than leaving a field that cannot work and
  // reports the switch as a fault.
  const [interpretDisabled, setInterpretDisabled] = useState(false);
  // Bumped when an add lands, so the fact editor can close itself — see `factAddedNonce`.
  const [factAddedNonce, setFactAddedNonce] = useState(0);
  const runInterpret = useCallback(
    (fieldKey: string, args: { text: string; priorProposal?: unknown; answer?: string },
     onProposal: (r: CrmFactInterpretResponse) => void) => {
      setInterpretError(null);
      interpretFact.mutate({ entryId, fieldKey, ...args }, {
        onSuccess: onProposal,
        // §6: surfaced inline, and the slot controls stay usable — never a spinner that hangs.
        onError: (err) => {
          const detail = (err as { detail?: { detail?: { error?: string; message?: string } } })
            ?.detail?.detail;
          if (detail?.error === 'interpret_disabled') {
            // Not a failure to report: nobody has switched it on. Withdraw the box; the slot controls
            // below are the documented fallback and are already usable.
            setInterpretDisabled(true);
            setInterpretError(null);
            return;
          }
          setInterpretError(detail?.message
            ?? 'The assistant is unavailable — fill this in by hand.');
        },
      });
    }, [interpretFact, entryId]);

  // §1.2: the TYPE change. Its own endpoint, because `relationship_type` is inside `fact_key` — this is
  // a refute plus a new predecessor-linked fact, not an edit, and the server will not pretend otherwise.
  const retypeFact = useRetypeProjectedFact(listId);
  const onRetypeFact = useCallback(
    (fk: string, fact: CrmProjectedFact, relationshipType: string,
     qualifiers: Record<string, string>) => {
      if (!begin(fk)) return;
      setActionError(null);
      retypeFact.mutate({ entryId, assertionId: fact.assertion_id,
                          expectedRevision: fact.revision, relationshipType, qualifiers }, {
        onError: (err) => applyError(err, 'Could not change the relationship.'),
        onSuccess: (d) => {
          rec.refetch(); recV2.refetch();
          if (d?.reprojection_incomplete) {
            setActionError('Changed, but the list cell could not be refreshed — reload to see it.');
          }
        },
        onSettled: end,
      });
    }, [retypeFact, entryId, applyError, begin, end, rec, recV2]);

  // §3/P4: ADD — a relationship the machine never proposed. Its own mutation because there is no chip
  // and no CAS. The refusal worth surfacing verbatim is the previously-REJECTED value: the ledger drops
  // that write, and the server turns it into a message naming the recovery rather than a silent success.
  const addFact = useAddProjectedFact(listId);
  // ⚠ The TYPE now comes from the modal, not from the column. The old signature took only an entity and
  // read `relationship_type` off the column's options — which is exactly why a catch-all could not add
  // anything: four types, no way to say which. The modal asks, so the caller states it.
  const onAddFact = useCallback((fieldKey: string, relationshipType: string, localId: number,
                                 qualifiers: Record<string, string>, reason?: string) => {
    const opts = data?.projected_add_options?.[fieldKey];
    if (!opts || !begin(fieldKey)) return;
    setActionError(null);
    addFact.mutate({ entryId, relationshipType,
                     // §2: the operator's own sentence when the add came from the describe-it box, so
                     // the audit reconstructs what was typed → proposed → accepted.
                     fromKind: opts.from_kind, localId, qualifiers, reason }, {
      onError: (err) => applyError(err, 'Could not add that value.'),
      onSuccess: (d) => {
        rec.refetch(); recV2.refetch();
        setFactAddedNonce((n) => n + 1);
        if (d?.reprojection_incomplete) {
          setActionError('Added, but the list cell could not be refreshed — reload to see it.');
        }
      },
      onSettled: end,
    });
  }, [addFact, data, entryId, applyError, begin, end, rec, recV2]);

  const onRejectProposal = useCallback((fieldKey: string, proposalId: number | null) => {
    // the mutation fires on modal confirm; label the modal with the column label, not the raw key.
    const label = columns.find((c) => c.field_key === fieldKey)?.label ?? fieldKey;
    setFieldRejectTarget({ entryId, fieldKey, proposalId, label });
  }, [columns, entryId]);

  const fieldRejectConfirm = useCallback((reason: string, note?: string) => {
    const target = fieldRejectTarget;
    if (!target || target.proposalId == null) { setFieldRejectTarget(null); return; }
    // belt-and-braces: the render gate already unmounts this modal on a repoint, so reaching here
    // with a stale target would mean a new path around that — drop the write rather than guess.
    if (target.entryId !== entryId) { setFieldRejectTarget(null); return; }
    if (!begin(target.fieldKey)) return;
    dismissProposal.mutate(
      { entryId, proposalId: target.proposalId, reason, note, cas: reviewCas },
      {
        onSuccess: () => setFieldRejectTarget(null),
        onError: (err) => { setFieldRejectTarget(null); applyError(err, 'Could not reject the proposal.'); },
        onSettled: end,
      });
  }, [fieldRejectTarget, entryId, dismissProposal, reviewCas, applyError, begin, end]);

  // Item 3b: a proposed record's whole-record action bar (Accept / Pass / Dismiss / Merge).
  // `_proposed_record` rides on the grid snapshot + the hydrated record alike.
  //
  // `source_id` (the phase_b_candidate uuid) used to be read here as the merge SOURCE key, because the
  // merge spoke `(deal_kind, deal_key)`. The native merge is entry→entry, so the source key IS
  // `entryId` and the candidate uuid is no longer part of this surface's vocabulary.
  const proposedRecord = (record as Record<string, unknown> | undefined)?._proposed_record as
    { source?: string; source_id?: string } | undefined;
  const isProposed = !!proposedRecord;
  const recordName = cellText((record as Record<string, unknown> | undefined)?.name) || 'this record';
  const noteContext = useMemo(
    () => noteMentionContext(record as Record<string, unknown> | undefined, columns),
    [record, columns],
  );
  const anyRecordActionPending =
    acceptRecord.isPending || passRecord.isPending || dismissRecord.isPending;
  // A record-level Accept / Pass can promote the record and STILL refuse individual fields: the server
  // re-checks the governed write rules at publish time, so a draft Deal Team whose member left the roster
  // between the draft edit and the Accept is reported in `materialized.refused` and published NOWHERE —
  // and the draft overlays are cleared regardless (a committed row must carry none). Closing the panel on
  // that response is silent data loss: the operator sees success and their typed value is gone. So we
  // keep the panel OPEN, name each refused field with the server's reason, and say plainly that the
  // record was promoted without them — they are editable on the committed row now, under the same rule.
  // Returns true when it handled a refusal (i.e. the caller must NOT close).
  const reportRefusedFields = useCallback((
    d: { materialized?: { refused?: { field_key: string | null; reason: string }[] } } | undefined,
    promoted: string,
  ): boolean => {
    const refused = d?.materialized?.refused ?? [];
    if (refused.length === 0) return false;
    const detail = refused
      .map((r) => `${r.field_key ?? 'a field'}: ${r.reason}`)
      .join(' · ');
    setActionError(
      `${promoted}, but ${refused.length} field${refused.length === 1 ? '' : 's'} could not be saved — `
      + `${detail}. Re-enter ${refused.length === 1 ? 'it' : 'them'} on the record now that it is committed.`,
    );
    rec.refetch();
    recV2.refetch();
    return true;
  }, [rec, recV2]);
  const acceptRecordFlow = useCallback(() => {
    if (!window.confirm(
      `Accept “${recordName}” and commit it as a deal on this list? The reviewed field values are `
      + `saved to the record. Nothing is sent anywhere else.`)) return;
    setActionError(null);
    // Echo the R2b CAS (when the v2 review is active) so a stale proposal-set → 409 rather than
    // promoting an out-of-date curation; applyError refetches + prompts re-apply on 409.
    acceptRecord.mutate({ entryId, cas: reviewCas }, {
      onSuccess: (d) => { if (!reportRefusedFields(d, 'The record was accepted')) nav.onClose(); },
      onError: (e) => applyError(e, 'Could not accept the record — the deal was not logged.'),
    });
  }, [recordName, acceptRecord, entryId, reviewCas, applyError, nav, reportRefusedFields]);
  const dismissRecordConfirm = useCallback((reason: string, note?: string) => {
    setActionError(null);
    dismissRecord.mutate({ entryId, reason, note }, {
      onSuccess: () => { setDismissFor(null); nav.onClose(); },
      onError: (e) => setActionError((e as { detail?: { detail?: string } } | null)?.detail?.detail
        ?? 'Could not dismiss the record.'),
    });
  }, [dismissRecord, entryId, nav]);
  const passRecordConfirm = useCallback((body: { category: string; note?: string }) => {
    setActionError(null);
    // Echo the R2b CAS exactly as acceptRecordFlow does. Pass is TERMINAL, so promoting a curation
    // that moved since render is the least recoverable of the four record actions — and until now this
    // was the one that sent no token, which is what left the server's CAS unreachable. `applyError`
    // turns the 409 into a refetch of both reads + "re-apply your change", as everywhere else.
    passRecord.mutate({ entryId, category: body.category, note: body.note, cas: reviewCas }, {
      // Pass materializes the same curated review as Accept, so it can refuse fields the same way.
      onSuccess: (d) => {
        setPassFor(null);
        if (!reportRefusedFields(d, 'The record was passed')) nav.onClose();
      },
      // CLOSE the modal on failure, and this is the load-bearing half of wiring the CAS at all.
      // `applyError` refetches both reads, so `reviewCas` re-arms with the FRESH token while the modal
      // is still mounted with the operator's category already chosen — a blind second click would then
      // sail through the CAS and terminally promote the curation they never saw. The modal staying open
      // turned the guard into a one-click speed bump. Closing it forces a re-open against the refreshed
      // panel; the banner `applyError` set says why.
      onError: (e) => { setPassFor(null); applyError(e, 'Could not pass the record — nothing was logged.'); },
    });
  }, [passRecord, entryId, reviewCas, applyError, nav, reportRefusedFields]);
  // Merge now targets a NATIVE list entry, not a candidate uuid / Affinity opp id. `CrmDealMergeModal`
  // owns the whole ritual (governed picker → preview → conflict choices → commit) and its own mutation,
  // so this call site only has to say what happens afterwards: the source row is a redirect now, so the
  // panel closes rather than re-rendering a record that no longer stands on its own.
  //
  // ⛔ THE OLD PICKER COULD NOT SEE THE TARGET. `useMergeRecord` → `merge-record` takes a
  // `(deal_kind, deal_key)` pair, and its picker offers open candidates plus the frozen Affinity
  // snapshot — which excludes every committed native deal, because Accept sets the candidate to
  // `status='promoted'`. That is why two "Project Alamo" rows could both be on screen with no way to
  // fold them together. The route still exists for its other callers; this surface no longer uses it —
  // including for TWO AI PROPOSALS: since owner ruling R1 (2026-10-26) the native picker offers another
  // draft when this row is a draft ("Flood Form" / "FloodForm"), so a draft pair never needs the
  // pre-cut route, which would strand the CRM-side rows and has no preview or undo. The survivor stays
  // a proposal; the panel still closes, because THIS row is the redirect either way.
  const mergeRecordConfirm = useCallback(() => {
    setMergeFor(null);
    nav.onClose();
  }, [nav]);
  // S3/S4: a COMMITTED record with actionable pending AI updates gets a sticky-header "Accept all" —
  // the record-level analogue of resolving each pending field. Gated on the v2 payload's own
  // `record_actions`/`pending_count` (computed together server-side, S3) so the label can NEVER show a
  // dead "(0)" — `record_actions` includes 'accept' iff `pending_count > 0`, by construction.
  const committedPending = !isProposed && !!v2Adapted?.recordActions?.includes('accept');
  const pendingCount = v2Adapted?.pendingCount ?? 0;
  const acceptAllFlow = useCallback(() => {
    setActionError(null);
    acceptAll.mutate({ entryId, cas: reviewCas }, {
      onSuccess: (d) => {
        rec.refetch(); recV2.refetch();
        const skipped = (d as { skipped?: number } | undefined)?.skipped;
        if (skipped) setActionError(`Accepted the pending updates; ${skipped} were skipped (superseded — reload to review).`);
      },
      onError: (e) => applyError(e, 'Could not accept the pending updates.'),
    });
  }, [acceptAll, entryId, reviewCas, rec, recV2, applyError]);
  // P1: dispose of the record's open AI suggestions in one act. Sticky server-side — an aggregator run
  // that re-proposes the same values does not bring them back — so the operator does not have to keep
  // dismissing the same thing. The record itself is untouched.
  const dismissAllAiFlow = useCallback(() => {
    setActionError(null);
    setDismissAiArmed(false);
    dismissAllAi.mutate({ entryId, cas: reviewCas }, {
      onSuccess: (d) => {
        rec.refetch(); recV2.refetch();
        const { skipped = 0 } = (d ?? {}) as { skipped?: number };
        // Not an error, but the only place to say it: a skipped field is one a newer run superseded
        // between render and click, and it is still sitting there pending.
        if (skipped) setActionError(`Dismissed the AI suggestions; ${skipped} were skipped `
          + '(a newer run superseded them — reload to review).');
      },
      onError: (e) => applyError(e, 'Could not dismiss the AI suggestions.'),
    });
  }, [dismissAllAi, entryId, reviewCas, rec, recV2, applyError]);
  const recordActions = isProposed ? (
    <div className={styles.recordProposedActions}>
      <button className={styles.recordAcceptBtn} onClick={acceptRecordFlow}
        disabled={anyRecordActionPending} title="Accept → log deal">✓ Accept</button>
      <button className={styles.recordPassBtn} onClick={() => setPassFor(entryId)}
        disabled={anyRecordActionPending} title="Pass → log at stage Passed">Pass</button>
      <button className={styles.recordDismissBtn} onClick={() => setDismissFor(entryId)}
        disabled={anyRecordActionPending} title="Dismiss this proposed record">Dismiss</button>
      <button className={styles.recordMergeBtn} onClick={() => setMergeFor(entryId)}
        disabled={anyRecordActionPending}
        title="Merge into an existing deal or another AI proposal">Merge</button>
    </div>
  ) : (
    /* A COMMITTED record always gets a bar now, because Merge belongs on it.
     *
     * ⛔ WHY IT WAS MISSING. #1149 put Merge only on the PROPOSED bar, and a committed record with no
     * pending AI updates rendered `undefined` — no bar at all. So the two rows the owner most needed to
     * fold together (16 `Digested Organics` and 633 `Project Grapefruit` — the same company-name-vs-
     * codename split that produced the Alamo pair) had no affordance, even though the backend has
     * supported committed→committed since #1149 and #1152 shipped the grid predicate specifically for
     * it. The gate was cosmetic, not structural.
     *
     * The Accept-all / Dismiss-AI pair stays conditional on `committedPending` — a dead "(0)" is what
     * that gate exists to prevent — while Merge is unconditional, because a duplicate is not something
     * the record tells you about in advance.
     *
     * Merge is NOT styled as a primary action. The ritual lives in the modal (preview the rows that
     * would move, decide every field disagreement, read BOTH names back in the confirm button), which
     * is where it belongs for an identity-terminal operation; a quiet button plus a real confirm beats a
     * prominent button plus a shallow one. */
    <div className={styles.recordProposedActions}>
      {committedPending && (<>
      <button className={styles.recordAcceptBtn} onClick={acceptAllFlow}
        disabled={acceptAll.isPending || dismissAllAi.isPending}
        title="Accept all pending AI updates on this record">✓ Accept all ({pendingCount})</button>
      {/* P1: two-step rather than a fifth modal — there is no input to collect, only an intent to
          confirm, and throwing away N suggestions must never be one click. */}
      {dismissAiArmed ? (
        <>
          <button className={`${styles.recordDismissBtn} ${styles.recordDismissArmed}`}
            onClick={dismissAllAiFlow} disabled={dismissAllAi.isPending}
            title="Confirm: dismiss these AI suggestions">
            {dismissAllAi.isPending ? 'Dismissing…' : `Dismiss ${pendingCount}?`}
          </button>
          <button className={styles.recordCancelBtn} onClick={() => setDismissAiArmed(false)}
            disabled={dismissAllAi.isPending}>Cancel</button>
        </>
      ) : (
        <button className={styles.recordDismissBtn} onClick={() => setDismissAiArmed(true)}
          disabled={acceptAll.isPending || dismissAllAi.isPending}
          title="Dismiss this record's AI suggestions — they will not be re-proposed unless the value changes">
          ⊘ Dismiss AI
        </button>
      )}
      </>)}
      <button className={styles.recordMergeBtn} onClick={() => setMergeFor(entryId)}
        disabled={anyRecordActionPending}
        title="Merge this deal into another — the other one survives">Merge</button>
    </div>
  );

  const onSaveLayout = useCallback((
    sections: CrmRecordLayoutSection[], hiddenFieldKeys: string[],
  ) => {
    setLayoutError(null);
    setRecordLayout.mutate({ sections, hiddenFieldKeys }, {
      onSuccess: () => setLayoutEditorOpen(false),
      onError: (e) => {
        const detail = (e as { detail?: { detail?: string } } | null)?.detail?.detail;
        setLayoutError(typeof detail === 'string' ? detail : 'Could not save the layout.');
      },
    });
  }, [setRecordLayout]);

  // E1a review finding 2: the shared `_invalidateRecordSurfaces` refreshes grid + record + record-v2,
  // but the timeline's SETTLED lanes are their own queries — the per-field version history and the
  // notes list — and neither was invalidated. So an accepted update left the pending queue and did not
  // reappear as history for up to the 60s staleTime: on the one surface built to reassure the operator
  // that their decision landed, it read as data loss. Same class as the invalidation bug fixed in the
  // IOT panel-cleanup wave. Prefix keys, so all three field-history streams and any notes page match.

  // ── Panel sub-views (D5 Updates + D6 Timeline/Notes). Both bridges come ONLY from the hydrated
  // endpoint payload (never the grid snapshot) → the sub-view switcher is hydrate-gated like the other
  // actionable affordances (§6.5). `rtab` is REPLACE-mode (nav-spec §7/§14): a push here would write a
  // null history.state entry that clobbers the `rec` {emberSel} marker + break close-to-list.
  const dealRef = data?.deal_ref ?? null;       // deal entry → Updates (D5) + Notes (D6)
  const subjectRef = data?.subject_ref ?? null; // person/org entry → Timeline (D6, subject's timeline)
  const [rtab, setRtab] = useViewParam('rtab', 'fields', { history: 'replace' });

  // Available sub-views by record type (Fields is always present). Deal + subject are mutually
  // exclusive per record (an opportunity has no subject; a person/org entry has no deal_ref).
  //
  // E1b-2 (owner ruling 2026-08-21): with `record_timeline` ON there is NO sub-tab split — the
  // timeline is a section of the Fields body (below) and Evidence / Notes / the interim `stream` tab
  // all go. `Updates` is the one survivor, because it is not a timeline lane at all: it is the D5/R6
  // embedded Deal-Updates workflow (log / push / log-pass / dismiss / merge / custom instructions,
  // the drift banners, the conveyor on ‹ ›). The ruling was "kill the tab link and text, at minimum
  // make it admin-only", so admin-only it is — and that narrowing is deliberately NOT flag-gated,
  // because it was asked for in its own right and the flag may sit off for a while.
  // `capabilities.is_admin`, NOT `auth/admins.isAdmin` (imported above for the layout editor): that
  // one is a hardcoded ADMIN_EMAILS mirror, while the server's `require_admin` also honours
  // `team_roster.is_admin`. FE admin gates read the capability — the same rule CrmListGrid states.
  const viewerIsAdmin = useMyTabs().data?.capabilities?.is_admin === true;
  const subTabs: { key: string; label: string }[] = [{ key: 'fields', label: 'Fields' }];
  // Evidence: the raw deal-evidence timeline, on BOTH a proposed candidate (isProposed) AND a
  // committed opp (deal_ref) — the CRM-governed /evidence endpoint resolves the binding server-side.
  // (Interim step toward the consolidated single view; committed rows also still show the fuller
  // Updates card until Phase 2 retires the sub-tab split.)
  const isDealRecord = isProposed || dealRef != null;
  // The capability mirrors `crm_record_timeline_enabled`, and it decides the panel's SHAPE: off keeps
  // the historical sub-tab split byte-for-byte (minus the Updates narrowing), on gives the one-scroll
  // panel. One switch, one reversible act.
  const recordTimelineEnabled = data?.capabilities?.record_timeline === true;
  // WP1 / S1.2 — the parent PORTFOLIO COMPANY (portco-transactions spec §2.2). Deal records only:
  // a person or organisation entry is not a transaction against a portco. It is a sub-view rather
  // than a field because parentage is a LINK with provenance and confidence, not a cell — and
  // because `create_field` on the IOT is still `_native_list`-gated, so a field was never available.
  // ⛔ Unconditional on `recordTimelineEnabled`: the link is not part of the timeline's one-scroll
  // consolidation, so hiding it behind that flag would make the panel's answer to "whose transaction
  // is this?" depend on a switch about something else.
  if (isDealRecord) subTabs.push({ key: 'portco', label: 'Portco' });
  if (isDealRecord && !recordTimelineEnabled) subTabs.push({ key: 'evidence', label: 'Evidence' });
  if (dealRef != null && viewerIsAdmin) subTabs.push({ key: 'updates', label: 'Updates' });
  if (dealRef != null && !recordTimelineEnabled) subTabs.push({ key: 'notes', label: 'Notes' });
  if (subjectRef != null && !recordTimelineEnabled) subTabs.push({ key: 'timeline', label: 'Timeline' });
  const hasSubViews = subTabs.length > 1;
  // A stale/invalid rtab (e.g. `updates` on a person entry after a list→list jump) falls back to Fields.
  const activeTab = subTabs.some((t) => t.key === rtab) ? rtab : 'fields';

  const subViewNav = hasSubViews ? (
    <div className={styles.recordSubTabs} role="tablist" aria-label="Record sub-views">
      {subTabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key}
          className={`${styles.recordSubTab} ${activeTab === t.key ? styles.recordSubTabActive : ''}`}
          onClick={() => setRtab(t.key)}>{t.label}</button>
      ))}
    </div>
  ) : undefined;

  let bodyOverride: ReactNode | undefined;
  if (activeTab === 'evidence' && isDealRecord) {
    // Raw evidence timeline — the deal's bound emails/meetings, governed by the /evidence endpoint.
    bodyOverride = (
      <div className={styles.recordTimelineBody}>
        <CrmEvidenceTimeline listId={listId} entryId={entryId} />
      </div>
    );
  } else if (activeTab === 'portco' && isDealRecord) {
    // The section is LAZY (`enabled`) like the evidence read — it fetches when the sub-view opens.
    bodyOverride = (
      <div className={styles.recordTimelineBody}>
        <CrmPortcoLink listId={listId} entryId={entryId} />
      </div>
    );
  } else if (activeTab === 'updates' && dealRef != null) {
    bodyOverride = (
      <CrmRecordDealUpdates
        dealKey={dealRef.deal_key}
        // Conveyor mapped onto the panel's ‹ › set: advance to the next record; when the set is
        // exhausted, close to the grid (mirrors the Deals "next pending" terminal behavior).
        onNext={() => (nav.hasNext ? nav.onNext() : nav.onClose())}
        onClose={nav.onClose}
      />
    );
  } else if (activeTab === 'notes' && dealRef != null && !recordTimelineEnabled) {
    // D6 (legacy shape only): the list_entry Notes surface. With the timeline on, notes render in the
    // stream and are CREATED from its "+ Note" — F.1's ruling, satisfied by the layout.
    bodyOverride = <div className={styles.recordNotesBody}><CrmNotesSection kind="list_entry" id={entryId}
      hostLabel={recordName} mentionContext={noteContext} /></div>;
  } else if (activeTab === 'timeline' && subjectRef != null && !recordTimelineEnabled) {
    // D6: person/org entries embed the SUBJECT's governed CrmTimeline (email/meeting stream). E3 adds
    // the note stream + type/date filter to this same surface.
    bodyOverride = (
      <div className={styles.recordTimelineBody}>
        {/* F.2: a revealed participant navigates through the panel's existing profile opener, so the
            panel's own §7 teardown (sel swap drops rec/rtab) applies unchanged. */}
        <CrmTimeline
          kind={subjectRef.kind}
          id={subjectRef.id}
          hostLabel={recordName}
          // timeline-inline spec §3.3: the row no longer has an `open` button — this is the
          // "Full view ↗" anchor inside an OPENED body. On the profile it is a `?sel=msg:N` push;
          // here it stays the panel's existing `onOpenMsg` (the mail modal over the panel), which
          // is what this surface has always done and what the ⌘-click href resolves to anyway.
          // It converges when the modal retires (§6) and this embed adopts the row wholesale.
          // ⛔ PASSED THROUGH AS-IS, never `?? (() => {})` (review F5): a no-op opener would
          // `preventDefault` the anchor and leave a control that does nothing at all.
          openMsgFullView={onOpenMsg}
          openPerson={nav.onOpenProfile ? (pid) => nav.onOpenProfile?.('person', pid) : undefined}
        />
      </div>
    );
  }

  // E1b-2 — THE TIMELINE AS A PANEL SECTION. Deal records get the consolidated stream; person/org
  // entries get the SUBJECT's entity timeline, which was the other tab the ruling retired ("no tabs"
  // cannot mean "no tabs on deal records only"). Undefined while the flag is off, so nothing mounts
  // and no lane fetches — the same structural guarantee the `stream` sub-tab used to give.
  //
  // ⚠ It IS a behaviour change once the flag flips: the lanes fetch when the record opens rather than
  // when a tab is clicked, so three audited reads now say "opened this record". Nothing new is
  // disclosed (same viewer, same governed doors, one click away before) — see the note on
  // `CrmRecordTimeline`.
  const timelineSection = !recordTimelineEnabled ? undefined
    : isDealRecord ? (
      <div className={styles.recordTimelineBody}>
        {/* ⛔ `key={entryId}` — the timeline REMOUNTS on a ‹ › hop (adversarial review 2026-09-05,
            B-4). Belt to `useCrmRecordEvidence`'s record-scoped `placeholderData` braces: the hook
            now refuses to serve the previous deal's payload, and this makes sure no in-component
            state (open bodies, the lane split, a scroll position) survives the hop either. Both,
            because either alone leaves half the surface stale — and the reset-during-render dance the
            two children do is what a `key` expresses properly. */}
        <CrmRecordTimeline key={entryId} listId={listId} entryId={entryId}
          hostLabel={recordName} mentionContext={noteContext} />
      </div>
    ) : subjectRef != null ? (
      <div className={styles.recordTimelineBody}>
        <CrmTimeline
          kind={subjectRef.kind}
          id={subjectRef.id}
          hostLabel={recordName}
          // timeline-inline spec §3.3: the row no longer has an `open` button — this is the
          // "Full view ↗" anchor inside an OPENED body. On the profile it is a `?sel=msg:N` push;
          // here it stays the panel's existing `onOpenMsg` (the mail modal over the panel), which
          // is what this surface has always done and what the ⌘-click href resolves to anyway.
          // It converges when the modal retires (§6) and this embed adopts the row wholesale.
          // ⛔ PASSED THROUGH AS-IS, never `?? (() => {})` (review F5): a no-op opener would
          // `preventDefault` the anchor and leave a control that does nothing at all.
          openMsgFullView={onOpenMsg}
          openPerson={nav.onOpenProfile ? (pid) => nav.onOpenProfile?.('person', pid) : undefined}
        />
      </div>
    ) : undefined;

  return (
    <>
      {/* WP-7 — ONE party selection for the whole record: `CrmPartiesField` (inside the fields grid)
          sets it through "show emails", `CrmRecordTimeline` (the section below) renders the chips and
          filters on it. The provider is host-scoped on `entryId`, so a ‹ › hop cannot carry one
          deal's parties onto the next deal's evidence fetch. */}
      <CrmPartyFilterProvider entryId={entryId}>
      <CrmRecordPanel
        {...nav}
        historyIds={{ listId, entryId }}
        record={record}
        columns={columns}
        fieldStates={fieldStates}
        editMode={editMode}
        options={options}
        refOptions={refOptions}
        stageLadders={stageLadders}
        loading={rec.isLoading}
        notFound={notFound}
        actionError={actionError}
        busyField={busyField}
        onEditField={onEditField}
        onRenameDraft={onRenameDraft}
        onAcceptProposal={onAcceptProposal}
        onDismissProposal={onDismissProposal}
        reviewFields={reviewFields}
        reviewIsProposed={isProposedPayload}
        onDraftEdit={onReviewEditField}
        onRejectProposal={onRejectProposal}
        onRestoreProposal={onRestoreProposal}
        projectedFacts={data?.projected_facts}
        onInterpretFact={interpretDisabled ? undefined : runInterpret}
        onFactEditorClosed={() => setInterpretError(null)}
        factAddedNonce={factAddedNonce}
        interpretBusy={interpretFact.isPending}
        interpretError={interpretError}
        onRemoveFact={onRemoveFact}
        onConfirmFact={onConfirmFact}
        onRepointFact={onRepointFact}
        onSaveFactRole={onSaveFactRole}
        onRetypeFact={onRetypeFact}
        projectedAddOptions={data?.projected_add_options}
        onAddFact={onAddFact}
        layout={data?.layout ?? recordLayout.data}
        canEditLayout={canEditLayout}
        onEditLayout={() => { setLayoutError(null); setLayoutEditorOpen(true); }}
        subViewNav={subViewNav}
        bodyOverride={bodyOverride}
        timelineSection={timelineSection}
        recordActions={recordActions}
        recentUpdatesSection={
          /* S5b: on the v2 path the blocks render INLINE at their layout position, so this appended
             section would render them a SECOND time.

             ⚠ The gate MUST be `capabilities.record_review_v2`, i.e. the SAME server-authoritative
             signal (`_v2_enabled()`) that `get_record` used to decide whether to emit block COLUMNS —
             and it ships in the SAME payload as those columns, so the two flip atomically.

             An earlier version keyed on `!reviewFields` and was WRONG (adv-review: all four reviewers
             found it independently). `reviewFields` means "the v2 payload has ARRIVED", which is
             strictly LATER than "the payload changed shape": `v2Enabled` is derived FROM the v1
             response, so the v2 query cannot even dispatch until v1 resolves. That left a guaranteed
             render on EVERY cold open — and every ‹ › hop to an uncached record — where the block
             column was present but `reviewFields` was still undefined, painting the same bullets twice.
             `retry:false` plus the ignored recV2 error state made it permanent on any v2 failure.

             Consequence accepted deliberately: during a v2 outage a PENDING block is not reviewable
             here. That is consistent — with v2 down, `reviewFields` is undefined for SCALARS too, so
             the whole panel degrades to v1 rendering uniformly. Rendering the section as a fallback
             would restore the double render, which is worse than a uniformly degraded panel.

             Flag OFF keeps the section: it is the only place a block can be reviewed there. That is
             also why CrmRecentUpdates's per-block ✓ Accept is left intact — removing it would strip the
             dark path's only block-accept affordance (record-level Accept-all is v2-only). */
          isDealRecord && data?.recent_updates && !data?.capabilities?.record_review_v2 ? (
            <CrmRecentUpdates
              listId={listId}
              entryId={entryId}
              recentUpdates={data.recent_updates}
              onDone={() => { rec.refetch(); }}
            />
          ) : undefined
        }
      />
      </CrmPartyFilterProvider>
      {/* Item 3b: whole-record dismiss reuses the Deals-tab candidate reason taxonomy. Transient
          modal (useState, not URL state). */}
      <DismissReasonModal
        open={dismissFor === entryId}
        granularity="deal"
        dealKind="candidate"
        subjectLabel={recordName}
        submitting={dismissRecord.isPending}
        onClose={() => setDismissFor(null)}
        onConfirm={dismissRecordConfirm}
      />
      {/* §1.2: removing a FACT still confirms and still records why, but the question now lives INSIDE
          the fact editor rather than in a second dialog stacked on top of it. Same taxonomy (imported,
          not copied), and the note now travels with the reason — this call site passed `String(reason)`
          and dropped it, so "Other" collected a mandatory explanation and threw it away. The reason
          lands in `crm_relationship_assertion.decision_reason`, the row §3 calls the highest-value one. */}
      {/* R5: per-field reject on a proposed (draft) row — the FIELD_DISMISS_REASONS taxonomy (records
          learning feedback; publishes nothing). Transient (useState). */}
      <DismissReasonModal
        open={fieldRejectTarget?.entryId === entryId}
        granularity="field"
        subjectLabel={fieldRejectTarget?.label ?? ''}
        submitting={dismissProposal.isPending}
        onClose={() => setFieldRejectTarget(null)}
        onConfirm={fieldRejectConfirm}
      />
      {/* Item 3b Pass/Merge: promote-to-Passed + fold-into-existing-deal, reusing the Deals-tab
          Pass modal (LogPassModal owns the category radios). Merge is `CrmDealMergeModal` — the
          NATIVE entry→entry merge, which owns its own governed picker, preview and mutation.
          Transient modals (useState, not URL state). */}
      {passFor === entryId && (
        <LogPassModal
          subjectLabel={recordName}
          submitting={passRecord.isPending}
          onClose={() => setPassFor(null)}
          onSubmit={passRecordConfirm}
        />
      )}
      {mergeFor === entryId && (
        <CrmDealMergeModal
          listId={listId}
          entryId={entryId}
          sourceName={recordName}
          onClose={() => setMergeFor(null)}
          onMerged={mergeRecordConfirm}
        />
      )}
      {layoutEditorOpen && canEditLayout && (
        <CrmRecordLayoutEditor
          columns={columns}
          layout={data?.layout}
          saving={setRecordLayout.isPending}
          error={layoutError}
          onClose={() => setLayoutEditorOpen(false)}
          onSave={onSaveLayout}
        />
      )}
    </>
  );
}
